import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Dialog } from '@/components/ui/Dialog'
import { DialogTabs } from '@/components/ui/DialogTabs'
import { TextField } from '@/components/ui/TextField'
import { AuditLog } from '@/features/audit/AuditLog'
import {
  loungeQueryOptions,
  useDeleteLounge,
  useLounge,
  useRenameLounge,
} from '@/features/lounges/api'
import { errorMessage } from '@/lib/graphql'

type Tab = 'general' | 'audit'

/**
 * Lounge settings — the flagship modal the architecture doc describes, with the
 * calm left-tab layout it asks for.
 *
 * **Mods get in now, not just owners.** The audit log is a moderator's tool, and
 * putting it anywhere else would have meant a second settings surface. General
 * stays owner-only *inside* the modal, which is the honest shape: the door is
 * for moderators, one of the rooms behind it is not.
 */
export const Route = createFileRoute('/_app/l/$loungeSlug/settings')({
  beforeLoad: async ({ context: { queryClient }, params: { loungeSlug } }) => {
    const { lounge } = await queryClient.ensureQueryData(loungeQueryOptions(loungeSlug))
    if (lounge?.myRole !== 'OWNER' && lounge?.myRole !== 'MOD') {
      throw redirect({ to: '/l/$loungeSlug', params: { loungeSlug } })
    }
  },
  component: LoungeSettings,
})

function LoungeSettings() {
  const { loungeSlug } = Route.useParams()
  const navigate = useNavigate()
  const { data } = useLounge(loungeSlug)

  const lounge = data?.lounge
  const isOwner = lounge?.myRole === 'OWNER'

  const [tab, setTab] = useState<Tab>(isOwner ? 'general' : 'audit')
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  // Held here rather than inside the General panel: the confirm dialog
  // *replaces* this one, so the mutation has to outlive the panel that started
  // it.
  const remove = useDeleteLounge()

  const close = () => void navigate({ to: '/l/$loungeSlug', params: { loungeSlug } })

  if (!lounge) return null

  const tabs = [
    ...(isOwner ? ([{ id: 'general', label: 'General' }] as const) : []),
    { id: 'audit', label: 'Audit log' },
  ] as const

  return (
    <>
      <Dialog
        open={!confirmingDelete}
        onClose={close}
        title="Lounge settings"
        description={`Everyone here is a member of ${lounge.name}.`}
      >
        <DialogTabs<Tab> tabs={tabs} active={tab} onChange={setTab}>
          {tab === 'general' && isOwner ? (
            <General
              loungeId={lounge.id}
              name={lounge.name}
              slug={lounge.slug}
              onDelete={() => setConfirmingDelete(true)}
              onClose={close}
            />
          ) : (
            <AuditLog loungeId={lounge.id} />
          )}
        </DialogTabs>
      </Dialog>

      {/* Replaces the settings dialog rather than stacking on it — one modal at
       * a time, never two scrims. */}
      <ConfirmDialog
        open={confirmingDelete}
        onCancel={() => setConfirmingDelete(false)}
        onConfirm={() => remove.mutate(lounge.id, { onSuccess: () => void navigate({ to: '/' }) })}
        pending={remove.isPending}
        title={`Delete ${lounge.name}?`}
        description="The lounge and everyone's membership of it go away. This cannot be undone."
        confirmLabel="Delete lounge"
      />
    </>
  )
}

function General({
  loungeId,
  name: current,
  slug,
  onDelete,
  onClose,
}: {
  loungeId: string
  name: string
  slug: string
  onDelete: () => void
  onClose: () => void
}) {
  const rename = useRenameLounge()
  const [name, setName] = useState(current)

  return (
    <>
      <div className="dialog-body">
        <TextField
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          hint={`The web address stays /l/${slug} — links people already have keep working.`}
        />

        {rename.isError && (
          <p className="form-error" role="alert">
            {errorMessage(rename.error, 'That name could not be saved.')}
          </p>
        )}
      </div>

      <div className="dialog-actions">
        <Button variant="quiet" onClick={onDelete}>
          Delete lounge
        </Button>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="primary"
          disabled={rename.isPending || name === current}
          onClick={() => rename.mutate({ loungeId, name })}
        >
          {rename.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </>
  )
}
